import { css, html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { formCss } from '../css/form';
import { resetCss } from '../css/reset';
import { stackCss } from '../css/stack';
import { typeCss } from '../css/type';
import { wordListPromise } from '../wordList';
import '../wordListElement';
import { SpellingBee } from './spellingBee';
import { clusterCss } from '../css/cluster';

// Only lower case letters.
const normalizeInput = (pattern: string) =>
  pattern.toLowerCase().replace(/[^a-z]/g, '');

@customElement('mw-spelling-bee-page')
export class JumblePageElement extends LitElement {
  @state() loading = true;
  @state() centerLetter = 'b';
  @state() outerLetters = 'erimsp';
  @state() solutions: string[] = [];

  private readonly spellingBee = wordListPromise.then(words => new SpellingBee(words));

  constructor() {
    super();
    this.updateWords();
  }

  onCenterLetterChange(event: InputEvent) {
    const target = event.target as HTMLInputElement;
    target.value = normalizeInput(target.value);
    this.centerLetter = target.value;
    this.updateWords();
  };

  onOuterLettersChange(event: InputEvent) {
    const target = event.target as HTMLInputElement;
    target.value = normalizeInput(target.value);
    this.outerLetters = target.value;
    this.updateWords();
  };

  async updateWords() {
    this.loading = true;
    const spellingBee = await this.spellingBee;
    this.solutions = spellingBee.matches(this.centerLetter, this.outerLetters);
    this.loading = false;
  };

  static get styles() {
    return [
      resetCss,
      stackCss,
      clusterCss,
      typeCss,
      formCss,
      css`
        .cluster { gap: var(--space-md); }
        #center-letter { width: 3ch; text-align: center; }
        #outer-letters { width: 8ch; text-align: center; }
        :invalid {
          background: #ffdfdf;
        }
      `
    ];
  }

  render() {
    return html`
      <div class="stack">
        <p>
          Enter the letters for a <a
          href="https://www.nytimes.com/puzzles/spelling-bee">Spelling Bee</a>
          puzzle to see potential solutions.
        </p>

        <div class="cluster">
          <div>
            <label for="center-letter">Center letter</label>
            <input
                id="center-letter"
                class="puzzle-input"
                autofocus
                required
                maxlength="1"
                spellcheck="false"
                .value="${this.centerLetter}"
                @input="${this.onCenterLetterChange}">
          </div>

          <div>
            <label for="outer-letters">Six outer letters</label>
            <input
                id="outer-letters"
                class="puzzle-input"
                spellcheck="false"
                required
                minlength="6"
                maxlength="6"
                .value="${this.outerLetters}"
                @input="${this.onOuterLettersChange}">
          </div>
        </div>

        ${!this.centerLetter ? html`<p>Enter the puzzle's center letter.</p>` : ''}
        ${!this.outerLetters ? html`<p>Enter the puzzle's six outer letters.</p>` : ''}

        <div>
          <h2>${this.solutions.length} potential words</h2>

          ${this.loading ? html`<p>Loading&hellip;</p>` : null}

          ${!this.loading && this.outerLetters.length && this.solutions.length === 0 ? html`
            <p>
              🤔 We can't find a way to make words with these letters.
              "${this.outerLetters}".
            </p>
          ` : null}

          <mw-word-list .words="${this.solutions}"></mw-word-list>
        </div>
      </div>
    `;
  }
}
